import { Controller } from "@hotwired/stimulus"

// Docs sidebar: section dropdown + product pill-tabs (user section only) +
// collapsible categories. Registered as "docs--sidebar". Mirrors the markup in
// packs/docs/app/views/doc_pages/_sidebar.html.erb exactly — adding a target
// or action here without updating the partial (or vice versa) will silently
// break the selector.
export default class extends Controller {
  static targets = [
    "sectionLabel",
    "sectionDescription",
    "dropdownPanel",
    "sectionOption",
    "productTabs",
    "productTab",
    "categoryGroup",
    "categoryContent",
    "categoryChevron",
  ]

  static values = {
    currentSection: { type: String, default: "user" },
    currentProduct: String,
    docSection: String,
    docProduct: String,
    sections: Object,
  }

  connect() {
    this._sections = this._readSections()
    const savedSection = this._getCookie("docs_section")
    if (savedSection && Object.keys(this._sections).includes(savedSection)) {
      this.currentSectionValue = savedSection
    } else if (this.hasDocSectionValue && this.docSectionValue) {
      this.currentSectionValue = this.docSectionValue
    }

    this._boundOutsideClick = this._handleOutsideClick.bind(this)
    this._clearStaleCollapsedState()
    this._applySection()
    this._initProductTab()
    // NOTE: Do NOT auto-restore collapsed state on navigation. The user
    // complaint was: "click on a doc → all categories collapse." Cause:
    // _restoreCollapsedState re-applied a stale `collapsed` state saved
    // while on a different product. Per-page-load behaviour is now:
    // (1) the server pre-expands the active doc's category;
    // (2) _applyProduct force-expands every category of the active product.
    // Within the SAME page lifetime the user can still collapse/expand
    // explicitly (toggleCategory). We just don't persist that across
    // navigations, so the sidebar always opens "useful" by default.
  }

  disconnect() {
    document.removeEventListener("click", this._boundOutsideClick)
  }

  toggleDropdown(event) {
    event?.stopPropagation()
    if (!this.hasDropdownPanelTarget) return
    const open = !this.dropdownPanelTarget.classList.contains("hidden")
    if (open) {
      this._closeDropdown()
    } else {
      this._openDropdown()
    }
  }

  selectSection(event) {
    const section = event.currentTarget.dataset.section
    if (!section || !Object.keys(this._sections).includes(section)) return

    this.currentSectionValue = section
    this._setCookie("docs_section", section)
    this._applySection()
    this._initProductTab()
    this._closeDropdown()
  }

  selectProduct(event) {
    const product = event.currentTarget.dataset.product
    if (!product) return
    this.currentProductValue = product
    this._setCookie("docs_product", product)
    this._applyProduct()
  }

  toggleCategory(event) {
    const category = event.currentTarget.dataset.category
    if (!category) return
    const content = this.categoryContentTargets.find(
      (el) => el.dataset.category === category,
    )
    const chevron = this.categoryChevronTargets.find(
      (el) => el.dataset.category === category,
    )
    if (!content) return
    const isHidden = content.classList.toggle("hidden")
    if (chevron) chevron.classList.toggle("-rotate-90", isHidden)
    // Persist within the current page only — clearing localStorage on
    // navigation prevents the "every category collapses" UX bug.
  }

  // --- private ---

  _readSections() {
    // The layout hands the translated section catalogue in (CONEK-6525). No
    // English fallback here: with nothing passed, `_applySection` finds no
    // meta and leaves the server-rendered label untouched.
    return this.hasSectionsValue ? this.sectionsValue || {} : {}
  }

  _applySection() {
    const section = this.currentSectionValue
    const meta = this._sections[section]
    if (!meta) return

    if (this.hasSectionLabelTarget) {
      this.sectionLabelTarget.textContent = meta.label
    }
    if (this.hasSectionDescriptionTarget) {
      this.sectionDescriptionTarget.textContent = meta.description
    }

    this.sectionOptionTargets.forEach((opt) => {
      const check = opt.querySelector("[data-check]")
      const active = opt.dataset.section === section
      if (check) check.classList.toggle("invisible", !active)
    })

    this.categoryGroupTargets.forEach((group) => {
      group.classList.toggle("hidden", group.dataset.section !== section)
    })

    // The product tab bar only exists for the user section.
    if (this.hasProductTabsTarget) {
      this.productTabsTarget.classList.toggle("hidden", section !== "user")
    }
  }

  _initProductTab() {
    if (this.currentSectionValue !== "user") return
    if (!this.hasProductTabTarget) return

    const available = this.productTabTargets
      .map((t) => t.dataset.product)
      .filter(Boolean)
    if (!available.length) return

    // Priority: current doc's product (if any) > saved cookie > first tab.
    // The live doc ALWAYS wins so "I'm on a CRM doc" → CRM tab is active.
    const docProduct = this.hasDocProductValue ? this.docProductValue : ""
    const saved = this._getCookie("docs_product")
    let chosen = available[0]
    if (docProduct && available.includes(docProduct)) {
      chosen = docProduct
    } else if (saved && available.includes(saved)) {
      chosen = saved
    } else if (
      this.hasCurrentProductValue &&
      this.currentProductValue &&
      available.includes(this.currentProductValue)
    ) {
      chosen = this.currentProductValue
    }

    this.currentProductValue = chosen
    this._applyProduct()
  }

  _applyProduct() {
    if (this.currentSectionValue !== "user") return
    const product = this.currentProductValue
    this.productTabTargets.forEach((tab) => {
      const active = tab.dataset.product === product
      tab.classList.toggle("bg-card", active)
      tab.classList.toggle("text-foreground", active)
      tab.classList.toggle("shadow-sm", active)
      tab.classList.toggle("text-foreground/60", !active)
      tab.classList.toggle("hover:text-foreground", !active)
    })
    this.categoryGroupTargets.forEach((group) => {
      if (group.dataset.section !== "user") return
      const matches = group.dataset.product === product
      group.classList.toggle("hidden", !matches)

      // Auto-expand categories belonging to the newly-active product so the
      // sidebar isn't just bare headers after a tab switch. User can still
      // collapse them manually; explicit toggles persist via localStorage.
      if (matches) {
        const content = group.querySelector(
          "[data-docs--sidebar-target='categoryContent']",
        )
        const chevron = group.querySelector(
          "[data-docs--sidebar-target='categoryChevron']",
        )
        if (content) content.classList.remove("hidden")
        if (chevron) chevron.classList.remove("-rotate-90")
      }
    })
  }

  _openDropdown() {
    if (!this.hasDropdownPanelTarget) return
    this.dropdownPanelTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      document.addEventListener("click", this._boundOutsideClick)
    })
  }

  _closeDropdown() {
    if (!this.hasDropdownPanelTarget) return
    this.dropdownPanelTarget.classList.add("hidden")
    document.removeEventListener("click", this._boundOutsideClick)
  }

  _handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this._closeDropdown()
    }
  }

  // The previous implementation persisted per-category collapsed/expanded
  // state to localStorage and re-applied it on every navigation. That caused
  // the regression Juan flagged: visiting a Portal doc after collapsing CRM
  // categories applied "collapsed" to every Portal category as well, making
  // the sidebar useless on every click. We now intentionally do NOT persist
  // collapse state across navigations; clear any leftover key once on connect
  // so existing users get a fresh, expanded sidebar.
  _clearStaleCollapsedState() {
    try {
      localStorage.removeItem("docs_category_state")
    } catch {
      // localStorage unavailable — nothing to clean up.
    }
  }

  _getCookie(name) {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name + "=([^;]*)"),
    )
    return match ? decodeURIComponent(match[1]) : null
  }

  _setCookie(name, value) {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/docs; max-age=31536000; SameSite=Lax`
  }
}
